/**
 * The Requests tab's connection to the Scheduling tab (Sofia, Aug 2026).
 *
 * A candidate writing "please propose times" used to be READ in this tab and SCHEDULED in another
 * — if the recruiter remembered to go there. When the request's candidate sits in one of the
 * board's scheduling pools, the expanded card now embeds the SAME view that pool's tab renders,
 * actions included: the case pool's whole role view (slot overview + this candidate's card — the
 * recruiter proposes times themselves, never a self-booking link), the final pool's FinalRoundCard
 * (Propose chains / Book), or — for the first-interview pool, whose invites are a per-role BATCH,
 * not a per-candidate card — a pointer to that tab.
 *
 * When a scheduling-intent request matches NO pool, the box says why instead of nothing, because
 * every miss has a different fix (see requestSchedulingMatch.ts). The loudest case is a stage that
 * feeds no pool: the fix is moving the candidate in Lever, and without the warning the recruiter
 * finds out only when nobody schedules anything. Non-scheduling requests render the card when the
 * candidate happens to be in a pool ("while you have them: they are waiting on an interview"), but
 * never the warning — a thanks-closing from someone in offer stage is not missing anything.
 */
import { type ReactNode, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type {
  InboundRequest,
  RoleBoard,
  SlotHold,
  StageRef,
  TrackingMap,
} from '../../types/taActionBoard';
import { FinalRoundCard } from './FinalRoundCard';
import { COLORS } from './format';
import type { ReplyTools } from './replyProposal';
import { isSchedulingIntent, resolveSchedulingMatch } from './requestSchedulingMatch';
import { RoleScheduling, type SchedulingTabAction } from './SchedulingTab';
import { useFinalRoundActions, type FinalRoundActionDeps } from './useFinalRoundActions';

export interface SchedulingConnectionCtx {
  roles: RoleBoard[];
  /** ALL Lever stages (BoardResponse.stages) — the interviewish check for the warning copy. */
  stages: StageRef[];
  /** ACTIVE Lever archive reasons — the post-debrief rejection. Byte-exact. */
  archiveReasons: string[];
  tracking: TrackingMap;
  /** opportunityId → "Interview confirmed" … — cards render muted. */
  handled: Record<string, string>;
  /** The signed-in user — panel suggestions read the kick-off sheet with their grant. */
  myEmail: string;
  /** Every 📅 action — role-level and per-candidate — into the board's modals (the view's switch). */
  onSchedulingAction: (role: RoleBoard, action: SchedulingTabAction) => void;
  /** Re-grade ONLY this role's panel calendars (the Re-check button). */
  onRecheck: (role: RoleBoard) => Promise<void>;
  /** postingId → calendar holds / leftover legacy holds — the slot overview shows both. */
  holds: Record<string, SlotHold[]>;
  legacyHolds: Record<string, number>;
  /** Everything the final-round booking cascade needs (shared with FinalRoundsSection). */
  finalRound: Omit<FinalRoundActionDeps, 'stages' | 'archiveReasons'>;
  /** Switch the board to a Scheduling sub-tab. */
  onGoToRound: (round: 'first' | 'case' | 'final') => void;
}

const ROUND_LABEL = { first: 'First interviews', case: 'Case interviews', final: 'Final rounds' } as const;

export function RequestSchedulingConnection({
  item,
  ctx,
  replyTools,
}: {
  item: InboundRequest;
  ctx: SchedulingConnectionCtx;
  /**
   * THIS request's reply editor (owned by InboundCard, which passes it in) — the mail asked for
   * times, so the reply carries the times, not a promise of them.
   */
  replyTools?: ReplyTools;
}) {
  // Unconditional: hooks may not hide behind the match. Each request card mounts its own modal
  // instance, so booking from here cannot fight the Final Rounds tab over one shared slot.
  const finalRound = useFinalRoundActions({
    ...ctx.finalRound,
    // Board-level facts, already on the ctx for the tab itself — the embedded
    // card offers the same post-debrief decision and needs the same two.
    stages: ctx.stages,
    archiveReasons: ctx.archiveReasons,
  });
  const [rechecking, setRechecking] = useState(false);
  const match = resolveSchedulingMatch(item, ctx.roles, ctx.stages);


  if (match.kind === 'case' || match.kind === 'final') {
    const { role, candidate } = match;
    return (
      <Frame round={match.kind} role={role} onGoToRound={ctx.onGoToRound}>
        {match.kind === 'case' ? (
          /*
           * The WHOLE role scheduling view the 📅 tab renders — slot overview (or the
           * configure-panel state) plus this candidate's card — not a booking link: the
           * recruiter proposes times themselves, from the same slots the tab shows.
           */
          <RoleScheduling
            role={role}
            handled={ctx.handled}
            tracking={ctx.tracking}
            myEmail={ctx.myEmail}
            onAction={ctx.onSchedulingAction}
            rechecking={rechecking}
            onRecheck={() => {
              setRechecking(true);
              void ctx.onRecheck(role).finally(() => setRechecking(false));
            }}
            holds={ctx.holds[role.postingId] ?? []}
            legacyHolds={ctx.legacyHolds[role.postingId] ?? 0}
            only={candidate.opportunityId}
            onProposeInReply={replyTools && ((slots) => replyTools.proposeSlots(slots))}
            /*
             * The live mirror: ticks on the card ARE the proposal, so every tick change —
             * including SlotList's mount with the pre-ticked suggestions — rewrites the times in
             * the reply, and unticking the last one puts the "I will come back" promise back.
             */
            onSelectionChange={replyTools && ((slots) => replyTools.syncProposalSlots(slots))}
            /*
             * The proxy may already have written times into the draft from this same grading
             * (item.proposal). Pre-tick exactly those, so the mount-time mirror confirms the
             * reply instead of rewriting it with a different top three.
             */
            initialSelection={
              item.proposal?.kind === 'proposed' ? item.proposal.slots.map((s) => s.start) : undefined
            }
            /*
             * Whenever the proxy has decided about this reply — times written, or the promise
             * deliberately kept with a reason — the mount must not write over that decision
             * (a "snoozed" amber box above a draft the mirror just filled with times). Only a
             * recruiter's tick does. Older payloads without `proposal` keep the mount mirror.
             */
            syncOnMount={!item.proposal}
          />
        ) : (
          <>
            <FinalRoundCard
              candidate={candidate}
              tracking={ctx.tracking}
              handledLabel={ctx.handled[candidate.opportunityId] || null}
              hasChains={(role.finalRounds.chains?.length ?? 0) > 0}
              onAction={(action) => {
                // The card never emits role-level fr-recheck; the type is just broad.
                if (action.type === 'fr-recheck') return;
                finalRound.onAction(role, action);
              }}
            />
            {finalRound.modals}
          </>
        )}
      </Frame>
    );
  }

  if (match.kind === 'first') {
    return (
      <Frame round="first" role={match.role} onGoToRound={ctx.onGoToRound}>
        <p className="rounded-lg bg-white p-3 text-xs text-gray-700 ring-1 ring-gray-200">
          <span className="font-semibold">{match.candidate.name}</span> is waiting for the
          first-interview invitation ({match.candidate.waitingDays.toFixed(1)}d). Invites for{' '}
          {match.role.roleTitle} go out as one batch from the First interviews tab.
        </p>
      </Frame>
    );
  }

  // A miss only speaks on requests whose ANSWER is scheduling; elsewhere it renders nothing.
  if (!isSchedulingIntent(item)) return null;
  const firstName = item.candidateName.split(' ')[0] || 'This candidate';
  const proposed = item.proposal?.kind === 'proposed';
  /**
   * The draft already carries times the proxy read from the role's panel calendars, and the ONLY
   * reason this board has no card is that the role is not on it. The amber "not in the pool"
   * warning would contradict a reply that answers; say where the times came from instead.
   *
   * Every other miss keeps the warning below even when times were proposed — a candidate the board
   * itself excludes (stage-resolved panel, Lever snooze, a stage it does not pool) is exactly the
   * case where the proxy and the Scheduling tab disagree, and the recruiter must hear it before
   * sending the times.
   */
  if (proposed && match.reason === 'role_not_on_board') {
    return (
      <div className="rounded-lg p-3 text-xs" style={{ backgroundColor: COLORS.cotton, color: '#52525B' }}>
        <span className="font-semibold">Scheduling: </span>
        The times in the reply come from the role’s case panel calendars. {firstName} has no card
        on this board’s Scheduling tab because the role is not on this board.
      </div>
    );
  }
  const copy: Record<typeof match.reason, string> = {
    no_lever: `${firstName} is not in Lever, so no scheduling pool can hold them — there is nothing to schedule from the board.`,
    role_not_on_board: `${firstName}'s role is not on this board, so the Scheduling tab has no card for them. Their pipeline lives with whoever owns the role.`,
    stage_not_interview: `${firstName} is in “${item.stageName || 'an unknown stage'}”, which feeds no scheduling pool — so this request has NO card on the Scheduling tab. If an interview should be scheduled, move them to the right stage in Lever first.`,
    not_in_pool: `${firstName} is in “${item.stageName || 'an interview stage'}” but in no scheduling pool right now — usually the interview is already booked, invited or snoozed. Check the Scheduling tab before promising times.`,
  };
  return (
    <div
      className="rounded-lg p-3 text-xs"
      style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
    >
      <p>
        <span className="font-semibold">Scheduling: </span>
        {proposed && 'The reply already lists times — read this before sending them. '}
        {copy[match.reason]}
        {item.leverUrl && (
          <>
            {' '}
            <a
              href={item.leverUrl}
              target="_blank"
              rel="noreferrer"
              className="font-semibold underline"
            >
              ↗ Lever
            </a>
          </>
        )}
      </p>
    </div>
  );
}

/**
 * The pool hit's frame: names WHICH Scheduling sub-tab this card is borrowed from and links there,
 * so the embed reads as a window into the other tab rather than a second scheduling surface.
 */
function Frame({
  round,
  role,
  onGoToRound,
  children,
}: {
  round: 'first' | 'case' | 'final';
  role: RoleBoard;
  onGoToRound: (round: 'first' | 'case' | 'final') => void;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold tracking-wide text-gray-500">
          {ROUND_LABEL[round]} · {role.roleTitle} — from your Scheduling tab
        </span>
        <Button size="sm" variant="outline" onClick={() => onGoToRound(round)}>
          Open tab
        </Button>
      </div>
      {children}
    </div>
  );
}
